import {
  type PinpointMappingType,
  pinpointStartMatchTargetFields,
} from '@/features/comment/models/pinpoint';
import { isBefore2025, isBefore2026 } from '@/features/project/_utils/project';
import type {
  ProjectBaseModel,
  ProjectModel,
} from '@/features/project/projects/_models/project';
import { isPS } from '@/utils';
import type { fundSheetTypeEnum, sheetTypeEnum } from '@/utils/enum';
import { isEqual } from 'lodash-es';
import type { z } from 'zod';
import { getScrollTargetPinpointFieldKey } from './get-scroll-target-pinpoint-field-key';
import type { KsInvestmentPinpointMappingType } from './ks-investment-pinpoint';
import type { KsLocalGovernmentPinpointMappingType } from './ks-local-government-pinpoint';
import type { KsStandardPinpointMappingType } from './ks-standard-pinpoint';
import type { RsPinpointMappingType } from './rs-pinpoint';
import type { RsSkippedReviewPinpointMappingType } from './rs-skipped-review-pinpoint';
import type { SsPinpointMappingType } from './ss-pinpoint';

export type PinpointSheetType =
  | z.infer<typeof sheetTypeEnum>
  | 'RS_SKIPPED_REVIEW'
  | 'KS_INVESTMENT'
  | 'KS_STANDARD'
  | 'KS_LOCAL_GOVERNMENT';

export const getPinpointSheetType = (
  sheetType: z.infer<typeof sheetTypeEnum>,
  fundSheetType: z.infer<typeof fundSheetTypeEnum> | undefined,
): PinpointSheetType => {
  return sheetType === 'KS'
    ? fundSheetType === 'investment'
      ? 'KS_INVESTMENT'
      : fundSheetType === 'standard'
        ? 'KS_STANDARD'
        : 'KS_LOCAL_GOVERNMENT'
    : sheetType;
};

export const getPinpointSceneAndFieldBySheetType = (args: {
  pinpointSheetType: PinpointSheetType;
  rs?: RsPinpointMappingType;
  ss?: SsPinpointMappingType;
  rsSkippedReview?: RsSkippedReviewPinpointMappingType;
  ksInvestment?: KsInvestmentPinpointMappingType;
  ksStandard?: KsStandardPinpointMappingType;
  ksLocalGovernment?: KsLocalGovernmentPinpointMappingType;
}): PinpointMappingType | undefined => {
  switch (args.pinpointSheetType) {
    case 'RS_SKIPPED_REVIEW':
      return args.rsSkippedReview ?? undefined;
    case 'RS':
      return args.rs ?? undefined;
    case 'KS_INVESTMENT':
      return args.ksInvestment ?? undefined;
    case 'KS_STANDARD':
      return args.ksStandard ?? undefined;
    case 'KS_LOCAL_GOVERNMENT':
      return args.ksLocalGovernment ?? undefined;
    default:
      return args.ss ?? undefined;
  }
};

/**
 * 年度に応じた「次回予算措置検討年度」/「直近の予算措置に基づき交付決定が想定される期間」の
 * pinpoint field を取得する。
 * 2026年度以降は旧項目と内容が異なる別項目として扱うため、pinpoint key も分けている。
 * すでに旧 key で登録されている2026年度以降のコメントは移行スクリプトで新 key に付け替える。
 *
 * NOTE: ピンポイントコメントは基金シート詳細画面にのみ存在し、project が必ず確定しているため
 * 引数は必須にしている（isBefore2026 のように project 未指定を許容すると、
 * 「未指定ならどちらの key か」という本来存在しない判断が生まれてしまう）。
 */
export const getNextBudgetMeasureReviewYearPinpointField = (
  project: Pick<z.infer<typeof ProjectBaseModel>, 'fiscalYear'>,
):
  | 'ks.standard.next-budget-measure-review-year'
  | 'ks.standard.expected-grant-decision-period' => {
  return isBefore2026(project)
    ? 'ks.standard.next-budget-measure-review-year'
    : 'ks.standard.expected-grant-decision-period';
};

export const isNotUsePinpoint = (
  project: z.infer<typeof ProjectModel> | undefined,
): { notUsePinpoint: boolean } => {
  const isKs = project?.sheetType === 'KS';
  const before2025 = isBefore2025({ fiscalYear: project?.fiscalYear ?? 0 });
  const notUsePinpoint = (isKs && before2025) || isPS();
  return { notUsePinpoint };
};

export const isMatchPinpoint = ({
  targetPinpoint,
  sourcePinpoint,
}: {
  targetPinpoint: PinpointMappingType;
  sourcePinpoint: PinpointMappingType;
}) => {
  const isMath = isEqual(targetPinpoint, sourcePinpoint);
  if (isMath) {
    return true;
  }
  if (targetPinpoint.field !== sourcePinpoint.field) {
    return false;
  }
  const startMatchTargetFieldKey = pinpointStartMatchTargetFields.includes(
    sourcePinpoint.field,
  );
  if (!startMatchTargetFieldKey) {
    return false;
  }
  const currentPinpointKey =
    getScrollTargetPinpointFieldKey({ pinpoint: sourcePinpoint }) ?? '';
  const isStartMatch = !!getScrollTargetPinpointFieldKey({
    pinpoint: targetPinpoint,
    mode: 'startMatchCheck',
    refsMap: { [currentPinpointKey]: null },
  });

  return isStartMatch;
};
